import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { ListScreen, Notice, TableSkeleton } from '../components/chrome/list-screen'
import { Screen, ScreenHeader } from '../components/chrome/shell'
import { Button } from '../components/ui/button'
import { ConfirmDialog, Dialog } from '../components/ui/dialog'
import { Field, Input, Select } from '../components/ui/field'
import { Blank, Panel, Row } from '../components/ui/panel'
import { Table, TBody, Td, Th, THead, Tr } from '../components/ui/table'
import { Chip, type Tone } from '../components/ui/tone'
import {
  ApiError,
  api,
  type ConnectOutcome,
  type CredentialState,
  type Domain,
  type Mailbox,
  type MailboxState,
  type Page,
  type ProviderHealth,
  type ProviderList,
} from '../lib/api'
import { useSession } from '../lib/session'

/**
 * Mailboxes (§20, §21, §54, §55).
 *
 * **This screen used to say a connect button was deliberately absent.** That was true until
 * 2026-08-21: a mailbox needs a provider account, a provider account needs somebody's credentials,
 * and neither the encryption mechanism (P002) nor an OAuth app existed. Both landed, so the button
 * is here and it goes somewhere real.
 *
 * What it does **not** do is pretend the rest is finished. Connecting produces a
 * `ProviderAccount` — an authorised Google identity Mail Room holds a refresh token for. Turning
 * that into a *mailbox* also needs a verified domain, and the screen says so rather than leaving
 * someone to wonder why their connected account sends nothing.
 */

const STATE_LABEL: Record<MailboxState, string> = {
  NEW: 'New',
  AUTHORIZING: 'Authorizing',
  VALIDATING: 'Validating',
  READY: 'Ready',
  ACTIVE: 'Active',
  COOLDOWN: 'Cooldown',
  PAUSED: 'Paused',
  DEGRADED: 'Degraded',
  AUTH_FAILED: 'Auth failed',
  DISABLED: 'Disabled',
  RETIRED: 'Retired',
}

const STATE_TONE: Record<MailboxState, Tone> = {
  NEW: 'neutral',
  AUTHORIZING: 'info',
  VALIDATING: 'info',
  READY: 'ok',
  ACTIVE: 'ok',
  COOLDOWN: 'warn',
  PAUSED: 'warn',
  DEGRADED: 'warn',
  AUTH_FAILED: 'bad',
  DISABLED: 'bad',
  RETIRED: 'neutral',
}

const CREDENTIAL_LABEL: Record<CredentialState, string> = {
  CONNECTED: 'Connected',
  EXPIRING: 'Expiring',
  REAUTH_REQUIRED: 'Reconnect needed',
  REVOKED: 'Revoked',
  ERROR: 'Error',
}

const VERDICT_TONE: Record<ProviderHealth['verdict'], Tone> = {
  OK: 'ok',
  DEGRADED: 'warn',
  BLOCKED: 'bad',
}

/**
 * What the operator is told when they come back from Google.
 *
 * Four outcomes, four different sentences. `declined` is not an error and must not read like one —
 * somebody pressed cancel, which is a decision the product should respect quietly.
 */
const OUTCOME: Record<ConnectOutcome, { tone: 'info' | 'warn' | 'bad'; text: string }> = {
  // `Notice` has no success tone, and adding one for a single banner would put a fourth colour in
  // a component whose whole job is to flag things that changed under the operator. `info` is what
  // this is: a statement of fact they asked for.
  ok: {
    tone: 'info',
    text: 'Connected. Mail Room now holds a refresh token for that Google account, encrypted, and can send as it.',
  },
  declined: {
    tone: 'warn',
    text: 'No account was connected — the consent screen was cancelled. Nothing was stored.',
  },
  failed: {
    tone: 'bad',
    text: 'Google refused the connection. Nothing was stored. The audit log has the reason; a stale link is the usual cause, and they last ten minutes.',
  },
  norefresh: {
    tone: 'bad',
    text: 'Google returned an access token but no refresh token, so the account was NOT stored — it would have stopped working within the hour. Disconnect Mail Room in that Google account, then connect again.',
  },
}

export function MailboxesScreen() {
  const { can } = useSession()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [outcome, setOutcome] = useState<ConnectOutcome | null>(null)
  const [creating, setCreating] = useState(false)
  const [connectingSmtp, setConnectingSmtp] = useState(false)
  const [stateFor, setStateFor] = useState<Mailbox | null>(null)

  /**
   * The callback lands on `/mailboxes?connect=…` as a full browser navigation from the API, so the
   * result arrives in the URL rather than in a response this app awaited.
   *
   * The parameter is stripped once read. Leaving it would make the banner reappear on every
   * refresh and on the back button, saying "connected" long after it stopped being news.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const connect = params.get('connect')
    if (!connect) return

    if (connect in OUTCOME) setOutcome(connect as ConnectOutcome)

    params.delete('connect')
    params.delete('provider')
    params.delete('account')
    const query = params.toString()
    window.history.replaceState(
      {},
      '',
      window.location.pathname + (query ? `?${query}` : ''),
    )
  }, [])

  const mailboxes = useQuery({
    queryKey: ['mailboxes'],
    queryFn: () => api.get<Page<Mailbox>>('/mailboxes?take=200'),
    enabled: can('view_contacts'),
  })

  const providers = useQuery({
    queryKey: ['providers'],
    queryFn: () => api.get<ProviderList>('/providers'),
    enabled: can('view_contacts'),
  })

  /**
   * Starts the flow. The API returns a URL rather than redirecting, so this app decides when the
   * operator leaves — a 302 answered to `fetch` would be followed by the fetch layer and land
   * Google's HTML in a JSON parser.
   */
  const connect = useMutation({
    mutationFn: () =>
      api.post<{ authorizeUrl: string }>('/providers/google/authorize', {
        returnPath: '/mailboxes',
      }),
    onSuccess: ({ authorizeUrl }) => {
      window.location.href = authorizeUrl
    },
  })

  const disconnect = useMutation({
    mutationFn: (id: string) =>
      api.post(`/providers/${id}/disconnect`, {
        reason: 'disconnected from the mailboxes screen',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['providers'] })
      void queryClient.invalidateQueries({ queryKey: ['mailboxes'] })
    },
  })

  if (!can('view_contacts')) {
    return (
      <Screen>
        <ScreenHeader title="Mailboxes" description="The accounts outreach sends from." />
        <Panel>
          <Blank
            icon="denied"
            title="You do not have access to mailboxes"
            body="Ask an administrator of your organization."
          />
        </Panel>
      </Screen>
    )
  }

  const rows = (mailboxes.data?.items ?? []).filter(
    (mailbox) => !search || mailbox.email.toLowerCase().includes(search.toLowerCase()),
  )

  const accounts = providers.data?.accounts ?? []
  const mayConnect = can('connect_credentials')
  // §56's split: putting a mailbox into service is a throughput decision, and creating one is an
  // identity decision. The API guards them with those two permissions and so does this screen.
  const mayOperate = can('change_mailbox_limits')

  return (
    <>
    <ListScreen
      title="Mailboxes"
      subtitle="The accounts outreach sends from, and the §21 ceilings each one runs under."
      query={search}
      onQuery={setSearch}
      queryPlaceholder="Filter mailboxes"
      primary={
        mayConnect ? (
          <Button
            onClick={() => connect.mutate()}
            disabled={connect.isPending}
            variant="primary"
          >
            {connect.isPending ? 'Opening Google…' : 'Connect a Google account'}
          </Button>
        ) : undefined
      }
      secondary={
        // Only once something could authenticate as it. A create form offered with no connected
        // account is a form whose only possible outcome is a refusal.
        mayConnect && accounts.some((account) => account.hasCredential) ? (
          <Button icon="plus" onClick={() => setCreating(true)}>
            New mailbox
          </Button>
        ) : undefined
      }
      notice={
        outcome ? (
          <Notice tone={OUTCOME[outcome].tone}>{OUTCOME[outcome].text}</Notice>
        ) : connect.isError ? (
          <Notice tone="bad">
            The connection could not be started: {String(connect.error)}. A missing
            CREDENTIAL_ENCRYPTION_KEY is the usual cause — Mail Room refuses to send anyone to a
            consent screen when it could not store the result.
          </Notice>
        ) : accounts.length === 0 ? (
          <Notice tone="info">
            No provider account is connected yet. Connecting one authorises Mail Room to send as a
            Google account — it never reads that mailbox, only sends (P038).
          </Notice>
        ) : undefined
      }
    >
      {/* ── connected accounts ──────────────────────────────────────────────
          Above the mailbox table on purpose: an account is the thing that has to exist first, and
          when nothing sends this is where the reason will be. */}
      {(accounts.length > 0 || mayConnect) && (
        <Panel
          className="mb-4"
          title="Connected accounts"
          description="What Mail Room holds a credential for. A mailbox sends through one of these."
          actions={
            mayConnect ? (
              <Button size="sm" onClick={() => setConnectingSmtp(true)}>
                Connect an SMTP server
              </Button>
            ) : undefined
          }
        >
          {accounts.length === 0 && (
            <div className="px-4 py-3">
              <p className="text-muted text-[12.5px]">
                Nothing is connected yet. Google needs one person to click Allow once; an SMTP
                server needs a hostname and a password, and no consent screen at all.
              </p>
            </div>
          )}
          {accounts.map((account) => (
            <Row key={account.id} label={account.externalAccountId ?? account.label}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                <Chip tone={VERDICT_TONE[account.verdict]}>
                  {CREDENTIAL_LABEL[account.state]}
                </Chip>
                <span className="text-faint text-[11.5px] tabular-nums">
                  {account.mailboxes.sending} of {account.mailboxes.total} sending
                </span>
                {mayConnect && account.hasCredential && (
                  <Button
                    size="sm"
                    variant="quiet"
                    onClick={() => disconnect.mutate(account.id)}
                    disabled={disconnect.isPending}
                  >
                    Disconnect
                  </Button>
                )}
              </div>
              {/* The derived reason, in the operator's words. Always present — when the verdict is
                  OK it says so, which is the difference between "fine" and "not checked". */}
              <p className="text-muted mt-1 text-[11.5px]">{account.reasons[0]}</p>
            </Row>
          ))}
        </Panel>
      )}

      {mailboxes.isLoading ? (
        <TableSkeleton columns={6} />
      ) : mailboxes.isError ? (
        <Blank
          icon="error"
          tone="bad"
          title="Mailboxes could not be loaded"
          action={<Button onClick={() => void mailboxes.refetch()}>Try again</Button>}
        />
      ) : rows.length > 0 ? (
        <Table>
          <THead>
            <Th>Address</Th>
            <Th width={130}>State</Th>
            <Th width={150}>Domain</Th>
            <Th width={130}>Provider</Th>
            <Th width={140} align="right">
              Today
            </Th>
            <Th width={110} align="right">
              Gap
            </Th>
            {mayOperate && <Th width={120} align="right" />}
          </THead>
          <TBody>
            {rows.map((mailbox) => (
              <Tr key={mailbox.id}>
                <Td mono>{mailbox.email}</Td>
                <Td>
                  <Chip tone={STATE_TONE[mailbox.state]}>{STATE_LABEL[mailbox.state]}</Chip>
                </Td>
                <Td className="text-muted">{mailbox.domain.fqdn}</Td>
                <Td className="text-muted">{mailbox.providerAccount.label}</Td>
                <Td align="right" className="tabular-nums">
                  {/* Sent against the ceiling, not a bare percentage — the ceiling is the number
                      an operator is actually deciding about. */}
                  {mailbox.sentToday} / {mailbox.dailyLimit}
                </Td>
                <Td align="right" className="text-muted tabular-nums">
                  {mailbox.minimumGapSec}–{mailbox.maximumGapSec}s
                </Td>
                {mayOperate && (
                  <Td align="right">
                    <Button size="sm" variant="quiet" onClick={() => setStateFor(mailbox)}>
                      {mailbox.state === 'RETIRED' ? 'Retired' : 'Change state'}
                    </Button>
                  </Td>
                )}
              </Tr>
            ))}
          </TBody>
        </Table>
      ) : search ? (
        <Blank
          icon="noResults"
          title={`No mailbox matches “${search}”.`}
          action={<Button onClick={() => setSearch('')}>Clear filter</Button>}
        />
      ) : accounts.length > 0 ? (
        <Blank
          icon="mailboxes"
          title="An account is connected, but no mailbox uses it yet."
          body="A mailbox is an address on a verified domain, sending through a connected account. Register and verify the domain first — a mailbox always belongs to one."
        />
      ) : (
        <Blank
          icon="mailboxes"
          title="No mailboxes are connected."
          body={
            mayConnect
              ? 'Connect a Google account to begin. A mailbox also needs a verified domain, so domains can be prepared in the meantime.'
              : 'Connecting an account needs the connect_credentials permission, which only a SUPER_ADMIN holds.'
          }
        />
      )}
    </ListScreen>

      <CreateMailboxDialog
        open={creating}
        accounts={accounts}
        onClose={() => setCreating(false)}
      />

      <MailboxStateDialog
        mailbox={stateFor}
        open={stateFor !== null}
        onClose={() => setStateFor(null)}
      />

      <ConnectSmtpDialog open={connectingSmtp} onClose={() => setConnectingSmtp(false)} />
    </>
  )
}
/**
 * The four states an operator may set, and what each one means in one line.
 *
 * A subset of `MailboxState`, and the subset is the design: `AUTHORIZING`, `VALIDATING`,
 * `COOLDOWN`, `DEGRADED` and `AUTH_FAILED` are states the *system* enters because something
 * happened. Offering them here would let an operator assert a fact rather than make a decision,
 * and the API refuses them for the same reason (§18, §19).
 */
const OPERABLE: Array<{
  state: MailboxState
  label: string
  detail: string
  destructive?: boolean
}> = [
  {
    state: 'READY',
    label: 'In service',
    detail: 'The planner may schedule sends against it.',
  },
  {
    state: 'ACTIVE',
    label: 'Active',
    detail: 'In service and sending.',
  },
  {
    state: 'PAUSED',
    label: 'Paused',
    detail: 'Temporarily out. Reversible, and it keeps its history and its §21 limits.',
  },
  {
    state: 'RETIRED',
    label: 'Retired',
    detail:
      'Permanently out. §44 treats bringing a retired mailbox back as an evasion pattern, so this cannot be undone — create a new mailbox instead.',
    destructive: true,
  },
]

/**
 * Putting a mailbox into service, taking it out, or retiring it.
 *
 * `POST /mailboxes/:id/state` landed on 2026-08-22 and nothing called it, so a mailbox could be
 * created through the console and never put to work through it — the first real send needed the
 * promotion written in SQL by hand (P047, G023).
 *
 * Retiring is separated from the other three rather than being a fourth button of the same weight:
 * it is the only one-way transition on the screen, and the confirmation carries the reason the API
 * requires anyway.
 */
function MailboxStateDialog({
  mailbox,
  open,
  onClose,
}: {
  mailbox: Mailbox | null
  open: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [retiring, setRetiring] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setReason('')
      setRetiring(false)
      setError(null)
    }
  }, [open])

  const setState = useMutation({
    mutationFn: ({ state, why }: { state: MailboxState; why: string }) =>
      api.post(`/mailboxes/${mailbox!.id}/state`, { state, reason: why || undefined }),
    onSuccess: async () => {
      setRetiring(false)
      await queryClient.invalidateQueries({ queryKey: ['mailboxes'] })
      await queryClient.invalidateQueries({ queryKey: ['providers'] })
      onClose()
    },
    onError: (e: Error) =>
      setError(
        e instanceof ApiError
          ? e.message
          : 'The mailbox state was not changed',
      ),
  })

  if (!mailbox) return null

  const retired = mailbox.state === 'RETIRED'

  return (
    <>
      <Dialog
        open={open && !retiring}
        title="Mailbox state"
        description={mailbox.email}
        width={520}
        onClose={onClose}
        footer={
          <Button variant="quiet" onClick={onClose}>
            Close
          </Button>
        }
      >
        <div className="space-y-3">
          <p className="text-muted text-[12.5px]">
            Currently <strong className="text-text">{STATE_LABEL[mailbox.state]}</strong> on{' '}
            <span className="font-mono text-[12px]">{mailbox.domain.fqdn}</span>.
          </p>

          {retired ? (
            <Notice tone="warn">
              This mailbox is retired. §44 treats bringing one back into service as an evasion
              pattern, so the API refuses every transition out of it — create a new mailbox
              instead.
            </Notice>
          ) : (
            <>
              <Field
                label="Reason"
                hint="Optional for the first three. Written to the audit log either way (§57)."
              >
                {(props) => (
                  <Input
                    {...props}
                    value={reason}
                    placeholder="rotating this address out of the March cohort"
                    onChange={(event) => setReason(event.target.value)}
                  />
                )}
              </Field>

              <div className="space-y-1.5">
                {OPERABLE.map((option) => (
                  <div
                    key={option.state}
                    className="border-line2 flex items-start gap-3 border-b py-2 last:border-b-0"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-text text-[12.5px]">{option.label}</p>
                      <p className="text-muted mt-0.5 text-[12px] text-pretty">{option.detail}</p>
                    </div>
                    <Button
                      size="sm"
                      variant={option.destructive ? 'dangerQuiet' : 'secondary'}
                      disabled={option.state === mailbox.state || setState.isPending}
                      onClick={() => {
                        setError(null)
                        if (option.destructive) {
                          setRetiring(true)
                        } else {
                          setState.mutate({ state: option.state, why: reason.trim() })
                        }
                      }}
                    >
                      {option.state === mailbox.state ? 'Current' : option.label}
                    </Button>
                  </div>
                ))}
              </div>

              {error && (
                <p role="alert" className="text-bad text-[12.5px]">
                  {error}
                </p>
              )}

              <div className="bg-line h-px w-full" />

              <RepointSection
                mailbox={mailbox}
                reason={reason}
                onError={setError}
                onDone={onClose}
              />
            </>
          )}
        </div>
      </Dialog>

      <ConfirmDialog
        open={retiring}
        title={`Retire ${mailbox.email}?`}
        body="This cannot be undone. A retired mailbox is permanently out of service — §44 refuses to bring one back, because rotating a mailbox back in when reputation degrades elsewhere is exactly the pattern that rule exists to stop."
        confirmLabel="Retire permanently"
        requireReason
        reasonLabel="Why is this mailbox being retired?"
        reasonPlaceholder="replaced by outreach@ijspe.com"
        pending={setState.isPending}
        error={error}
        onCancel={() => setRetiring(false)}
        onConfirm={(why) => setState.mutate({ state: 'RETIRED', why })}
      />
    </>
  )
}

/**
 * Creating a mailbox — the join of an address, a connected account and a verified domain.
 *
 * `POST /mailboxes` has existed since 2026-08-22 and had no console either, which meant a
 * connected Google account could hold a working credential with nothing able to use it.
 *
 * The domain list is read from `GET /domains`, which is behind `manage_domains`. Everyone who may
 * reach this dialog holds `connect_credentials`, and the only role with that is SUPER_ADMIN, which
 * holds both — so the picker cannot be the reason somebody sees an empty list.
 */
function CreateMailboxDialog({
  open,
  accounts,
  onClose,
}: {
  open: boolean
  accounts: ProviderHealth[]
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [providerAccountId, setProviderAccountId] = useState('')
  const [domainId, setDomainId] = useState('')
  const [error, setError] = useState<string | null>(null)

  const domains = useQuery({
    queryKey: ['domains'],
    queryFn: () => api.get<Domain[]>('/domains'),
    enabled: open,
  })

  useEffect(() => {
    if (open) {
      setEmail('')
      setDisplayName('')
      setError(null)
      // One connected account is the normal case during the ICPK-only phase. Preselecting it
      // saves a click and, more usefully, means the field cannot be left empty by accident.
      const usable = accounts.filter((account) => account.hasCredential)
      setProviderAccountId(usable.length === 1 ? usable[0]!.id : '')
      setDomainId('')
    }
  }, [open, accounts])

  const create = useMutation({
    mutationFn: () =>
      api.post('/mailboxes', {
        email: email.trim(),
        providerAccountId,
        domainId,
        displayName: displayName.trim() || undefined,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['mailboxes'] })
      await queryClient.invalidateQueries({ queryKey: ['providers'] })
      onClose()
    },
    onError: (e: Error) =>
      setError(e instanceof ApiError ? e.message : 'The mailbox was not created'),
  })

  const usableAccounts = accounts.filter((account) => account.hasCredential)
  const domain = domains.data?.find((d) => d.id === domainId)
  const host = email.slice(email.lastIndexOf('@') + 1).trim().toLowerCase()
  // The API refuses this too, and says the same thing. Catching it here means the operator finds
  // out while they are still looking at both fields.
  const mismatched = Boolean(domain && host && host !== domain.fqdn)

  return (
    <Dialog
      open={open}
      title="New mailbox"
      description="An address on a verified domain, sending through a connected account. It starts out of service — putting it into service is a separate, audited act (§19)."
      width={520}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={
              !email.includes('@') ||
              !providerAccountId ||
              !domainId ||
              mismatched ||
              create.isPending
            }
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {usableAccounts.length === 0 && (
          <Notice tone="warn">
            No connected account holds a credential, so nothing could authenticate as this mailbox.
            Connect a Google account first.
          </Notice>
        )}

        <Field
          label="Address"
          error={mismatched ? `${email} is not on ${domain!.fqdn}.` : undefined}
          hint="Must be on the domain selected below — that is what ties it to the authentication that domain publishes."
        >
          {(props) => (
            <Input
              {...props}
              value={email}
              placeholder="outreach@ijspe.com"
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>

        <Field label="Display name" hint="What recipients see. Optional — the address alone is a valid From.">
          {(props) => (
            <Input
              {...props}
              value={displayName}
              placeholder="IJSPE Editorial Office"
              onChange={(event) => setDisplayName(event.target.value)}
            />
          )}
        </Field>

        <Field label="Sends through">
          {(props) => (
            <Select
              {...props}
              value={providerAccountId}
              onChange={(event) => setProviderAccountId(event.target.value)}
            >
              <option value="">Choose a connected account</option>
              {usableAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.externalAccountId ?? account.label} ({account.kind})
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field
          label="Domain"
          hint="A domain that is not READY can be chosen, and the planner will refuse to schedule against it until its DNS verifies (§18)."
        >
          {(props) => (
            <Select
              {...props}
              value={domainId}
              onChange={(event) => setDomainId(event.target.value)}
            >
              <option value="">Choose a domain</option>
              {(domains.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.fqdn} — {d.state.toLowerCase()}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {error && (
          <p role="alert" className="text-bad text-[12.5px]">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  )
}


/**
 * Which connected account sends as this address — §12, §54.
 *
 * `@@unique([organizationId, email])` makes one address one mailbox, so moving an address from a
 * Google OAuth account to an SMTP one is not a second mailbox — it is this one, repointed. There
 * was no way to do it outside SQL until 2026-08-25.
 *
 * The server refuses while the mailbox is in service or has messages in flight, and the reason
 * comes back in full rather than being restated here — it explains *why*, which a generic
 * "cannot repoint" would not.
 */
function RepointSection({
  mailbox,
  reason,
  onError,
  onDone,
}: {
  mailbox: Mailbox
  reason: string
  onError: (message: string | null) => void
  onDone: () => void
}) {
  const { can } = useSession()
  const queryClient = useQueryClient()
  const [target, setTarget] = useState('')

  const providers = useQuery({
    queryKey: ['providers'],
    queryFn: () => api.get<ProviderList>('/providers'),
    enabled: can('connect_credentials'),
  })

  const repoint = useMutation({
    mutationFn: () =>
      api.post(`/mailboxes/${mailbox.id}/provider-account`, {
        providerAccountId: target,
        reason,
      }),
    onSuccess: async () => {
      onError(null)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['mailboxes'] }),
        queryClient.invalidateQueries({ queryKey: ['providers'] }),
      ])
      onDone()
    },
    onError: (e: Error) =>
      onError(
        e instanceof ApiError ? e.message : 'The mailbox was not repointed',
      ),
  })

  if (!can('connect_credentials')) return null

  const candidates = (providers.data?.accounts ?? []).filter(
    (account) => account.id !== mailbox.providerAccount.id && account.hasCredential,
  )

  const inService = mailbox.state === 'ACTIVE' || mailbox.state === 'READY'

  const blockedBecause = inService
    ? `Pause ${mailbox.email} first — repointing a mailbox that is in service changes the sending path with nothing on screen to say so`
    : reason.trim().length < 8
      ? 'Write a reason of at least 8 characters above — §57 records it against your name'
      : !target
        ? 'Choose the account this address should send through'
        : null

  return (
    <div className="space-y-2">
      <p className="text-[12.5px]">Sending account</p>
      <p className="text-muted text-[12px] text-pretty">
        Currently <strong className="text-text">{mailbox.providerAccount.label}</strong> (
        {mailbox.providerAccount.kind}). Changing this changes which stored credential
        authenticates as {mailbox.email}.
      </p>

      {candidates.length === 0 ? (
        <p className="text-faint text-[12px]">
          No other connected account holds a credential, so there is nowhere to move this address.
        </p>
      ) : (
        <div className="flex items-end gap-3">
          <Field label="Move to" className="min-w-0 flex-1">
            {(props) => (
              <Select
                {...props}
                value={target}
                onChange={(event) => setTarget(event.target.value)}
              >
                <option value="">Choose an account…</option>
                {candidates.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.label} — {account.kind}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Button
            size="sm"
            variant="secondary"
            disabled={blockedBecause !== null || repoint.isPending}
            title={blockedBecause ?? undefined}
            aria-label={
              blockedBecause
                ? `Move sending account — unavailable: ${blockedBecause}`
                : 'Move sending account'
            }
            onClick={() => {
              onError(null)
              repoint.mutate()
            }}
          >
            {repoint.isPending ? 'Moving…' : 'Move'}
          </Button>
        </div>
      )}
    </div>
  )
}

/**
 * Connecting an SMTP server — §12, §116 build item 15.
 *
 * The other half of the provider story, and deliberately nothing like the Google button beside it:
 * no redirect, no consent screen, no coming back with a query parameter. A hostname, a port, a
 * username and a password, checked against the real server before anything is stored.
 *
 * **The port and the TLS mode are one choice, not two.** Offering a free-text port and a separate
 * "use TLS" switch invites 587-with-implicit-TLS, which fails with a timeout rather than an error
 * anybody can read. The two combinations that work are named; anything else is Custom, and then
 * the operator is on their own on purpose.
 */
function ConnectSmtpDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [host, setHost] = useState('')
  const [mode, setMode] = useState<'587' | '465' | 'custom'>('587')
  const [customPort, setCustomPort] = useState('')
  const [customSecure, setCustomSecure] = useState(false)
  const [username, setUsername] = useState('')
  const [credential, setCredential] = useState('')
  const [label, setLabel] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setHost('')
      setMode('587')
      setCustomPort('')
      setCustomSecure(false)
      setUsername('')
      setCredential('')
      setLabel('')
      setError(null)
    }
  }, [open])

  const port = mode === '587' ? 587 : mode === '465' ? 465 : Number(customPort)
  const secure = mode === '465' ? true : mode === '587' ? false : customSecure

  const connect = useMutation({
    mutationFn: () =>
      api.post<{ id: string; label: string; nextStep: string }>('/providers/smtp/connect', {
        host: host.trim(),
        port,
        secure,
        username: username.trim(),
        credential,
        label: label.trim() || undefined,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['providers'] })
      onClose()
    },
    // The API's message, not a generic one. It distinguishes a rejected password from an
    // unreachable host from a server that would not start TLS, and each sends an operator
    // somewhere different.
    onError: (e: Error) =>
      setError(e instanceof ApiError ? e.message : 'The account was not connected'),
  })

  const valid =
    // Any non-empty host. This required `host.includes('.')` until 2026-08-25, which rejected a
    // bare LAN hostname and every IPv6 literal — client-side validation second-guessing a server
    // that already returns a precise "could not be resolved" for exactly this case, and stores
    // nothing when it does.
    host.trim().length > 0 &&
    username.length > 0 &&
    credential.length > 0 &&
    Number.isInteger(port) &&
    port > 0 &&
    port <= 65535

  return (
    <Dialog
      open={open}
      title="Connect an SMTP server"
      description="Checked against the server before anything is stored. Nothing is saved if it does not authenticate."
      width={520}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!valid || connect.isPending}
            onClick={() => {
              setError(null)
              connect.mutate()
            }}
          >
            {connect.isPending ? 'Connecting…' : 'Connect'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Server" hint="The submission host, which is often not the same as the domain.">
          {(props) => (
            <Input
              {...props}
              value={host}
              placeholder="smtp.hostinger.com"
              onChange={(event) => setHost(event.target.value)}
            />
          )}
        </Field>

        <Field
          label="Port and encryption"
          hint="Port 25 is normally blocked outbound and is not offered."
        >
          {(props) => (
            <Select
              {...props}
              value={mode}
              onChange={(event) => setMode(event.target.value as typeof mode)}
            >
              <option value="587">587 — STARTTLS (usual)</option>
              <option value="465">465 — implicit TLS</option>
              <option value="custom">Something else</option>
            </Select>
          )}
        </Field>

        {mode === 'custom' && (
          <div className="flex items-end gap-3">
            <Field label="Port" className="w-[120px]">
              {(props) => (
                <Input
                  {...props}
                  value={customPort}
                  inputMode="numeric"
                  placeholder="2525"
                  onChange={(event) =>
                    setCustomPort(event.target.value.replace(/[^0-9]/g, ''))
                  }
                />
              )}
            </Field>
            <label className="flex cursor-pointer items-center gap-2 pb-2 text-[12.5px]">
              <input
                type="checkbox"
                checked={customSecure}
                onChange={(event) => setCustomSecure(event.target.checked)}
              />
              <span>Implicit TLS from the first byte</span>
            </label>
          </div>
        )}

        <Field label="Username" hint="Usually the full address of the mailbox.">
          {(props) => (
            <Input
              {...props}
              value={username}
              placeholder="outreach@ijspe.com"
              onChange={(event) => setUsername(event.target.value)}
            />
          )}
        </Field>

        <Field
          label="Password"
          hint="Sealed by the credential vault and never shown again. Some providers require an app-specific password."
        >
          {(props) => (
            <Input
              {...props}
              type="password"
              autoComplete="new-password"
              value={credential}
              onChange={(event) => setCredential(event.target.value)}
            />
          )}
        </Field>

        <Field label="Label" hint="What the console shows. Defaults to “user at host:port”.">
          {(props) => (
            <Input
              {...props}
              value={label}
              placeholder="IJSPE outreach relay"
              onChange={(event) => setLabel(event.target.value)}
            />
          )}
        </Field>

        {error && (
          <p role="alert" className="text-bad text-[12.5px]">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  )
}
